import * as THREE from 'three';

import type { TileData } from '../../../model/gamestate/GameState';
import { TerrainTextureRegistry } from './TerrainTextureRegistry';
import { getTileTerrainTextureKey, type TerrainMaterialSet } from './types';

export class TerrainMaterialFactory {
  public constructor(private readonly textures: TerrainTextureRegistry) {}

  public createTileMaterials(tile: TileData): THREE.MeshStandardMaterial[] {
    const spec = this.getMaterialSet(tile);
    const topMaterial = new THREE.MeshStandardMaterial({
      color: spec.top.color,
      map: spec.top.textureKey ? this.textures.getTexture(spec.top.textureKey) : null,
      roughness: spec.top.roughness,
      metalness: spec.top.metalness,
      transparent: spec.top.transparent,
      opacity: spec.top.opacity,
      emissive: new THREE.Color(spec.top.emissive),
      emissiveIntensity: spec.top.emissiveIntensity,
    });
    const sideMaterial = new THREE.MeshStandardMaterial({
      color: spec.side.color,
      roughness: spec.side.roughness,
      metalness: spec.side.metalness,
      transparent: spec.side.transparent,
      opacity: spec.side.opacity,
      emissive: new THREE.Color(spec.side.emissive),
      emissiveIntensity: spec.side.emissiveIntensity,
    });
    return [topMaterial, sideMaterial];
  }

  public createTileTopCapMaterial(tile: TileData): THREE.MeshBasicMaterial {
    const spec = this.getMaterialSet(tile);
    return new THREE.MeshBasicMaterial({
      color: spec.top.color,
      map: spec.top.textureKey ? this.textures.getTexture(spec.top.textureKey) : null,
      transparent: spec.top.transparent,
      opacity: spec.top.opacity,
      side: THREE.DoubleSide,
    });
  }

  private getMaterialSet(tile: TileData): TerrainMaterialSet {
    if (tile.terrain === 'portal') {
      return {
        top: {
          color: '#6d28d9',
          textureKey: null,
          roughness: 0.4,
          metalness: 0.08,
          transparent: false,
          opacity: 1,
          emissive: '#7c3aed',
          emissiveIntensity: 0.22,
        },
        side: {
          color: '#4c1d95',
          roughness: 0.5,
          metalness: 0.06,
          transparent: false,
          opacity: 1,
          emissive: '#5b21b6',
          emissiveIntensity: 0.14,
        },
      };
    }

    const topColor = this.getTileTopColor(tile);
    const isWater = tile.biome === 'water' || tile.biome === 'realistic_water';
    const sideColor = new THREE.Color(topColor).multiplyScalar(isWater ? 0.78 : 0.62).getStyle();

    return {
      top: {
        color: topColor,
        textureKey: getTileTerrainTextureKey(tile),
        roughness: isWater ? 0.18 : 0.86,
        metalness: isWater ? 0.22 : 0.06,
        transparent: isWater,
        opacity: isWater ? 0.9 : 1,
        emissive: '#000000',
        emissiveIntensity: 0,
      },
      side: {
        color: sideColor,
        roughness: isWater ? 0.32 : 0.92,
        metalness: isWater ? 0.18 : 0.04,
        transparent: isWater,
        opacity: isWater ? 0.82 : 1,
        emissive: '#000000',
        emissiveIntensity: 0,
      },
    };
  }

  private getTileTopColor(tile: TileData): string {
    if (tile.terrain === 'wall') {
      return '#334155';
    }
    switch (tile.biome) {
      case 'grass':
      case 'soft_grass':
        return '#ffffff';
      case 'sand':
        return '#f2d79a';
      case 'water':
        return '#dbeafe';
      case 'dirt':
        return '#d2b48c';
      case 'grassy_dirt':
      case 'brick':
      case 'brick2':
      case 'cobblestone_path':
      case 'realistic_grass':
      case 'realistic_grass2':
      case 'realistic_floor':
      case 'realistic_dirt':
        return '#ffffff';
      case 'realistic_water':
        return '#dbeafe';
      case 'default':
      default:
        return tile.walkable ? '#cbd5e1' : '#334155';
    }
  }
}
